import { useSearchParams } from 'react-router-dom'
import classNames from 'classnames'
import { groupBy } from 'lodash'
import moment from 'moment'
import { observer } from 'mobx-react-lite'
import { ReactElement, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery } from 'urql'
import { ContactDrawer } from '../contacts/ContactDrawer'
import { ContactInbox } from '../contacts/inbox/ContactInbox'
import { InboxHeader } from './InboxHeader'
import { InboxSkeleton } from './InboxSkeleton'
import { RelatedContacts } from './RelatedContacts'
import { SchedulingDockContext } from './scheduling-dock/SchedulingDockContext'
import { HAS_ACTIVE_AGENT_QUERY, THREAD_QUERY, UPDATE_THREAD_MUTATION, type HasActiveAgentResult } from './graphql'
import type { ContactJson } from 'app/types/entities'
import type { MessageJson } from 'app/types/entities'
import { normalizePhoneKey } from 'app/formatPhoneNumber'
import { notifyError } from 'app/services/notify.client'
import { useContactStore } from 'app/stores/StoreContext'
import { useMediaQuery } from 'app/hooks/useMediaQuery'
import { useMinimumLoading } from 'app/hooks/useMinimumLoading'
import { useEscapeListener } from 'app/hooks/useEscapeListener'
import { gqlQuery } from 'app/lib/graphqlClient'
import { OPS_URL } from 'app/lib/opsUrqlClient'
import { bg } from 'lib/background'
import { debug, logError } from 'lib/logging'
import { graphql } from 'app/generated/gql'

const CALLER_ID_QUERY = graphql(`
  query InboxCallerId($callerPhone: String!, $locationId: ID!) {
    callerId(callerPhone: $callerPhone, locationId: $locationId) {
      callerId
      customerName
    }
  }
`)

export function getUnreadMessageCount(messages?: MessageJson[]) {
  return (messages ?? []).filter((message) => message.sentByUserId == null && !message.isAgent && !message.read).length
}

function getIncomingMessageCount(messages?: MessageJson[]) {
  return (messages ?? []).filter((message) => message.sentByUserId == null && !message.isAgent).length
}

function usePageContent({
  contact,
  messages,
  conversationKey,
  selectedPhone,
  threadId,
  messagesLoading,
}: {
  contact?: ContactJson
  messages?: MessageJson[]
  conversationKey: string
  selectedPhone?: string
  threadId?: string
  messagesLoading?: boolean
}) {
  const [searchParams] = useSearchParams()
  const contactStore = useContactStore()
  const [focusedRelatedContact, setFocusedRelatedContact] = useState<ContactJson | undefined>()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [openDrawer, setOpenDrawer] = useState<boolean>(() => searchParams.has(`drawer`))
  const [relatedContacts, setRelatedContacts] = useState<ContactJson[] | undefined>()
  const [callCallerId, setCallCallerId] = useState<string | undefined>()

  const locationId = searchParams.get(`locationId`) ?? undefined

  const opsContext = useMemo(() => ({ url: OPS_URL }), [])
  const [agentQuery] = useQuery<HasActiveAgentResult>({
    query: HAS_ACTIVE_AGENT_QUERY,
    variables: { locationId },
    pause: !locationId,
    context: opsContext,
  })
  const hasActiveAgent: boolean | undefined =
    !locationId || agentQuery.fetching || agentQuery.error
      ? agentQuery.error
        ? false
        : undefined
      : (agentQuery.data?.aiAgentConfigurations?.some((c) => !!c.systemPrompt?.trim()) ?? false)

  // Prefer the thread id the controller already knows so the thread query fires
  // immediately, in parallel with the message fetch. Deriving it only from
  // `messages` made the query start late — after messages arrived — which flipped
  // `conversationReady` back to false and caused a second skeleton flash.
  const activeThreadId = threadId || messages?.find((message) => message.threadId)?.threadId

  // A new conversation runs on a predicted thread id the API only creates on the
  // first send, so `thread(id)` answering "Not found" is expected here, not a toast.
  const threadContext = useMemo(() => ({ suppressErrorToast: true }), [])
  const [threadQuery] = useQuery<{ thread: { id: string; aiEnabled: boolean; name?: string | null } | null }>({
    query: THREAD_QUERY,
    variables: { id: activeThreadId },
    pause: !activeThreadId,
    context: threadContext,
  })

  const [, updateThread] = useMutation(UPDATE_THREAD_MUTATION)

  // We know the conversation is ready to render only once every async input has
  // settled together: the workspace agent query, the thread query (if there's a
  // thread), AND the thread's messages. Gating on all of them means the surface
  // shows a single skeleton and then the finished content — no skeleton → spinner
  // → skeleton → content flicker as each piece resolves on its own schedule.
  // If there's no locationId, the agent query is paused — treat it as resolved.
  const conversationReady =
    (!locationId || agentQuery.data !== undefined) &&
    (!activeThreadId || (threadQuery.data !== undefined && !threadQuery.fetching)) &&
    !messagesLoading
  const aiHandling = hasActiveAgent === true && threadQuery.data?.thread?.aiEnabled === true
  // Thread-level AI Agent state for the message badges. Defaults to true while
  // loading so the active badge renders by default; falls through to false only
  // when the server confirms aiEnabled === false.
  const agentEnabled = threadQuery.data?.thread ? threadQuery.data.thread.aiEnabled !== false : undefined

  const handleTakeOver = async () => {
    if (!activeThreadId) return
    const result = await updateThread({ id: activeThreadId, input: { aiEnabled: false } })
    if (result.error) {
      notifyError(`Failed to take over from AI Agent`)
      logError(result.error.message)
    }
  }

  const cellPhone = useMemo(
    () => selectedPhone || contact?.cellPhone || messages?.find((m) => !m.sentByUserId && !m.isAgent)?.from,
    [selectedPhone, messages, contact],
  )

  const groupedMessages = useMemo(() => {
    if (!messages || messages.length === 0) return {}
    return groupBy(messages, (m: MessageJson) => {
      const date = moment.utc(m.createdDate).startOf(`day`)
      const today = moment().startOf(`day`)
      const diff = date.diff(today, `days`)
      if (diff === 0) return `Today`
      if (diff === -1) return `Yesterday`
      return date.format(`MMMM D, YYYY`)
    }) as Record<string, MessageJson[]>
  }, [messages])

  const setDrawer = (id: string | undefined | null) => {
    let url = new URL(document.location.href)
    if (id) url.searchParams.set(`drawer`, id)
    else url.searchParams.delete(`drawer`)
    history.replaceState({}, ``, url.toString())
  }

  const closeDrawerHandler = () => {
    setOpenDrawer(false)
    setFocusedRelatedContact(undefined)
    setDrawer(null)
  }

  const loadContact = () => {
    let url = new URL(document.location.href)
    debug(`inbox`, `loadContact`, searchParams.get(`drawer`))
    setFocusedRelatedContact(undefined)
    bg(async () => {
      let contactId = url.searchParams.get(`drawer`) || ``
      if (!contactId) {
        setOpenDrawer(false)
        return
      }
      const result = await contactStore.fetchOne(contactId)
      const loaded = (result.data as any)?.contact as ContactJson | undefined
      if (!loaded || result.error) {
        notifyError(`Contact not found`)
        url.searchParams.delete(`drawer`)
        history.replaceState({}, ``, url.toString())
        setOpenDrawer(false)
        return
      }
      // Render the drawer from the interned instance (ADR-0002), not the raw `loaded` copy,
      // so an edit merged into the shared instance (inbox card, contacts table, or a live
      // WebSocket event) reflects in the drawer in place. fetchOne already set `current` to
      // the interned instance for this id; fall back to `loaded` only if it is somehow unset.
      setFocusedRelatedContact(contactStore.current ?? loaded)
      setOpenDrawer(true)
    })
  }

  useEffect(() => {
    setRelatedContacts(undefined)
    setCallCallerId(undefined)
    setIsLoading(true)
  }, [conversationKey])

  useEffect(() => {
    if (!cellPhone) {
      setRelatedContacts(undefined)
      setIsLoading(false)
      return
    }

    bg(async () => {
      const locationId = searchParams.get(`locationId`)
      const locationFilter = locationId ? ` AND locationId:${locationId}` : ``
      const result = await contactStore.fetchList({ search: `cellPhone:${cellPhone}${locationFilter}` })
      const resp = result.data as any
      const contacts = resp?.contacts
      if (result.error) {
        logError(`Could not retrieve related contacts for ${cellPhone}`, result.error.message)
      } else if (contacts && contacts.total > 0) {
        // Hold the interned instance for each id (ADR-0002), not a fresh plain-object
        // projection. fetchList already interned every hit into the identity map, so an
        // edit merged into the shared instance (the drawer, the contacts table, or a live
        // WebSocket event) reflects in the related-contact cards in place. The hit returned
        // by the list read is itself the interned instance; `lookup` is the explicit read.
        setRelatedContacts(contacts.hits.map((c: ContactJson) => contactStore.lookup(c.id!) ?? c))
      } else {
        setRelatedContacts(undefined)
        debug(`inbox`, `related contacts`, `No related contacts found for ${cellPhone}`)
      }
      setIsLoading(false)
    })
  }, [cellPhone, conversationKey, searchParams])

  useEffect(() => {
    if (!cellPhone) return
    const locationId = searchParams.get(`locationId`)
    if (!locationId) return

    bg(async () => {
      const { data, error } = await gqlQuery(CALLER_ID_QUERY, { callerPhone: cellPhone, locationId })
      if (!error && data?.callerId) {
        setCallCallerId(data.callerId.customerName || data.callerId.callerId || undefined)
      }
    })
  }, [cellPhone, conversationKey, searchParams])

  useEffect(() => {
    if (searchParams.has(`drawer`)) {
      loadContact()
    }
  }, [])

  // The conversation surface is gated on AI/thread query readiness — its
  // properties are nested under a discriminated union so consumers can only
  // touch aiHandling / handleTakeOver / groupedMessages after proving the
  // status is `ready`. The right panel + contact drawer don't depend on AI
  // state and stay in the common return.
  const conversation: ConversationSurface = conversationReady
    ? { status: `ready`, groupedMessages, aiHandling, agentEnabled, handleTakeOver }
    : { status: `loading` }

  return {
    cellPhone,
    callCallerId,
    activeThreadId,
    threadName: threadQuery.data?.thread?.name,
    relatedContacts,
    isLoading,
    openDrawer,
    focusedRelatedContact,
    conversation,
    setDrawer,
    loadContact,
    closeDrawerHandler,
    setRelatedContacts,
  }
}

type ConversationSurface =
  | { status: `loading` }
  | {
      status: `ready`
      groupedMessages: Record<string, MessageJson[]>
      aiHandling: boolean
      /**
       * Thread-level `aiEnabled`. `undefined` while no thread is loaded.
       * Forwarded to <ContactInbox> so message badges + sidebar pills can
       * render the muted "AI Disabled" state.
       */
      agentEnabled: boolean | undefined
      handleTakeOver: () => Promise<void>
    }

type PageContentComponentProps = {
  conversationKey: string
  threadId?: string
  contact?: ContactJson
  messages?: MessageJson[]
  messagesLoading?: boolean
  selectedPhone?: string
  hasSmsAgentProduct?: boolean
  onMarkThreadRead?: () => void | Promise<void>
  onMarkThreadUnread?: () => void | Promise<void>
  /** Archive or unarchive the open thread. Absent on surfaces with no controller. */
  onArchiveThread?: () => void | Promise<void>
  threadArchived?: boolean
  onMessage: (message: MessageJson) => void
  // Mobile-only: return from the thread to the conversation list (master-detail).
  onBack?: () => void
} & ReturnType<typeof usePageContent>

const PageContentComponent = observer(function PageContentComponent({
  conversationKey,
  contact,
  messages,
  onMarkThreadRead,
  onMarkThreadUnread,
  onArchiveThread,
  threadArchived,
  hasSmsAgentProduct,
  onMessage,
  onBack,
  cellPhone,
  callCallerId,
  activeThreadId,
  threadName,
  conversation,
  relatedContacts,
  isLoading,
  openDrawer,
  focusedRelatedContact,
  setDrawer,
  loadContact,
  closeDrawerHandler,
  setRelatedContacts,
}: PageContentComponentProps): ReactElement {
  const contactStore = useContactStore()
  // Auto-collapse the right details panel below the xl breakpoint so the
  // conversation has breathing room on smaller screens. The chevron toggle still
  // lets the user open it manually until the next viewport change.
  const isWide = useMediaQuery(`(min-width: 1280px)`)
  const [detailsOpen, setDetailsOpen] = useState(isWide)

  const { draftRequest, dockContact } = useContext(SchedulingDockContext)

  // The dock deliberately survives thread switches, so a booking can finish
  // while a different conversation is on screen. Only hand the draft to the
  // composer when it was built for the thread being displayed, otherwise a
  // confirmation for one patient lands in another patient's thread. Compare
  // against the phone stamped on the request itself, never against the live
  // dockContact: the confirmation step leaves the dock open, so the staff
  // member can switch it to another contact before this renders, and a
  // coincidental match would then deliver the wrong text. Related contacts all
  // share the conversation's number, so the phone is the right comparison, not
  // the contact id. Withholding is not discarding: useDraftRequest records an
  // id only when it actually receives the request, so the draft arrives intact
  // the moment the staff member returns to the correct thread.
  const scopedDraftRequest =
    draftRequest && normalizePhoneKey(draftRequest.phone) === normalizePhoneKey(cellPhone) ? draftRequest : undefined

  // Below xl the details panel is an overlay covering the conversation. Opening
  // the dock from a card there would leave the widget behind the panel, so the
  // panel gets out of the way. At xl+ the panel is inline and stays put.
  // dockContact?.id, not dockContact: handleContactChange replaces a related
  // contact with a new spread object on any inline edit (e.g. a pipeline
  // change), so re-clicking Schedule on that same contact gives setDockContact
  // a new reference for the same logical contact. Depending on the whole
  // object would refire this and silently close a details panel the staff
  // member had deliberately reopened.
  useEffect(() => {
    if (dockContact && !isWide) setDetailsOpen(false)
  }, [dockContact?.id, isWide])

  // Keep the conversation skeleton on screen for a minimum window so a warm
  // revisit — where the AI/thread queries resolve instantly from cache — shows
  // the same transition as a cold load instead of blinking the skeleton. Keyed
  // on the conversation so switching threads replays the transition too.
  const showConversationLoading = useMinimumLoading(conversation.status === `loading`, {
    triggerKey: conversationKey,
  })
  // Details can also be opened and closed by the user on narrow screens.
  // oxlint-disable-next-line react/no-deriving-state-in-effects
  useEffect(() => {
    // Intentional synchronization: the drawer can also be opened or closed by the user.
    // oxlint-disable-next-line react/no-deriving-state-in-effects
    setDetailsOpen(isWide)
  }, [isWide])

  // When the drawer overlay is open on a narrow screen, ESC and clicks outside close it.
  const drawerRef = useRef<HTMLDivElement>(null)
  const showAsDrawer = detailsOpen && !isWide
  // Disarmed while the contact profile is open on top so one Escape closes the
  // topmost surface rather than both at once.
  useEscapeListener(() => setDetailsOpen(false), showAsDrawer && !openDrawer)
  useEffect(() => {
    if (!showAsDrawer) return
    const onMouseUp = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null
      if (!target || !drawerRef.current) return
      if (drawerRef.current.contains(target)) return
      // Ignore clicks on the chevron toggle itself — its own onClick handles state.
      if (target.closest(`[aria-label="Hide details"]`) || target.closest(`[aria-label="Show details"]`)) return
      // Ignore clicks in popovers/menus rendered in portals
      if (target.closest(`[data-radix-popper-content-wrapper]`) || target.closest(`[data-headlessui-portal]`)) return
      // The contact profile stacks on top of this panel, so its clicks are
      // outside this ref but must not collapse the list it was opened from.
      if (target.closest(`[x-component-name="Drawer"]`)) return
      setDetailsOpen(false)
    }
    window.addEventListener(`mouseup`, onMouseUp)
    return () => window.removeEventListener(`mouseup`, onMouseUp)
  }, [showAsDrawer])
  return (
    <div className="relative flex h-full overflow-hidden">
      {/* min-w-0: without it this grow flex item refuses to shrink below its
          content's min-width (the message area + composer), overflowing the
          viewport on mobile. */}
      <div className="flex min-w-0 grow flex-col divide-y divide-subtle">
        {conversation.status === `ready` && !showConversationLoading ? (
          <>
            <InboxHeader
              threadId={activeThreadId}
              cellPhone={cellPhone}
              contactName={contact?.name}
              threadName={threadName}
              relatedContacts={relatedContacts}
              onBack={onBack}
              unread={getUnreadMessageCount(messages)}
              incomingCount={getIncomingMessageCount(messages)}
              detailsOpen={detailsOpen}
              onToggleDetails={() => setDetailsOpen((prev) => !prev)}
              onMarkAllRead={onMarkThreadRead}
              onMarkUnread={onMarkThreadUnread}
              onArchive={onArchiveThread}
              isArchived={threadArchived}
            />
            <ContactInbox
              conversationKey={conversationKey}
              contact={contact}
              messages={conversation.groupedMessages}
              relatedContacts={relatedContacts}
              resolvedPhone={cellPhone}
              draftRequest={scopedDraftRequest}
              aiHandling={conversation.aiHandling}
              agentEnabled={conversation.agentEnabled}
              onTakeOver={conversation.handleTakeOver}
              onMessage={onMessage}
            />
          </>
        ) : (
          <InboxSkeleton />
        )}
      </div>
      {/* Must not be wrapped in a z-indexed element: a flex item with its own
          z-index forms a stacking context, which would clamp the drawer panel's
          `fixed z-[35]` to the wrapper's level. */}
      <ContactDrawer
        openDrawer={openDrawer}
        contact={focusedRelatedContact}
        onClose={closeDrawerHandler}
        onChange={loadContact}
      />
      <div
        ref={drawerRef}
        className={classNames(
          !detailsOpen && `hidden`,
          // Below xl the details panel is an overlay. On mobile (< md) it's a
          // full-screen sheet (inset-0 so the w-full inner fills the viewport);
          // on md–xl it docks to the right at its natural 380px width.
          // `z-30` is the app's sheet tier, below the contact drawer (z-[35])
          // so selecting a related contact opens the profile on top of here.
          isWide ? `z-0` : `fixed inset-0 z-30 shadow-elevation-8 md:inset-y-0 md:left-auto md:right-0`,
        )}
      >
        <RelatedContacts
          cellPhone={cellPhone}
          callerId={callCallerId}
          contactName={contact?.name}
          isLoading={isLoading}
          relatedContacts={relatedContacts}
          threadId={activeThreadId}
          hasSmsAgentProduct={hasSmsAgentProduct}
          hasPriorAIWorkflow={messages?.some((m) => !!m.aiWorkflowId) ?? false}
          onClose={() => setDetailsOpen(false)}
          onContactSelect={(contact) => {
            setDrawer(contact.id)
            loadContact()
          }}
          onContactCreated={(contact) => {
            // Prefer the interned instance for the new id (ADR-0002); fall back to the created
            // contact object itself (not a lean projection) so later edits still merge in place.
            setRelatedContacts((prev) => [...(prev ?? []), contactStore.lookup(contact.id!) ?? contact])
          }}
        />
      </div>
    </div>
  )
})

function PageContent(props: Omit<PageContentComponentProps, keyof ReturnType<typeof usePageContent>>) {
  const hookState = usePageContent({
    contact: props.contact,
    messages: props.messages,
    conversationKey: props.conversationKey,
    selectedPhone: props.selectedPhone,
    threadId: props.threadId,
    messagesLoading: props.messagesLoading,
  })
  return <PageContentComponent {...props} {...hookState} />
}

export { PageContent }
Object.assign(PageContent, { usePageContent })
